/**
 * Version Check Utility
 * 
 * Detects when a new version of the app is deployed and prompts user to reload
 * This prevents "Failed to fetch dynamically imported module" errors
 */

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_KEY = 'app_version';

/**
 * Get the current app version from meta tag or package.json
 */
const getCurrentVersion = (): string => {
  const metaVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
  return metaVersion || import.meta.env.VITE_APP_VERSION || '1.0.0';
};

/**
 * Check if a new version is available by fetching index.html
 */
const checkForNewVersion = async (): Promise<boolean> => {
  try {
    const response = await fetch('/index.html', {
      method: 'HEAD',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    const etag = response.headers.get('etag');
    const lastModified = response.headers.get('last-modified');

    const currentFingerprint = localStorage.getItem(VERSION_KEY);
    const newFingerprint = etag || lastModified || Date.now().toString();

    if (currentFingerprint && currentFingerprint !== newFingerprint) {
      console.log('[VersionCheck] New version detected!');
      return true;
    }

    // Store the fingerprint for next check
    localStorage.setItem(VERSION_KEY, newFingerprint);
    return false;
  } catch (error) {
    // Não logar erro de rede - é esperado quando offline
    const isNetworkError = 
      String(error).includes('Failed to fetch') ||
      String(error).includes('ERR_NAME_NOT_RESOLVED') ||
      String(error).includes('Network');

    if (!isNetworkError) {
      console.error('[VersionCheck] Failed to check version:', error);
    }
    return false;
  }
};

/**
 * Show a notification to user about new version
 */
const showUpdateNotification = () => {
  // Check if we already showed the notification recently
  const lastNotification = sessionStorage.getItem('update_notification_shown');
  if (lastNotification) {
    const timeSinceLastNotification = Date.now() - parseInt(lastNotification, 10);
    if (timeSinceLastNotification < 60000) { // Don't show again within 1 minute
      return;
    }
  }

  sessionStorage.setItem('update_notification_shown', Date.now().toString());

  // Create a simple notification
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px 20px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    z-index: 9999;
    max-width: 400px;
    animation: slideIn 0.3s ease-out;
  `;

  notification.innerHTML = `
    <style>
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    </style>
    <div style="display: flex; flex-direction: column; gap: 12px;">
      <div style="display: flex; align-items: start; gap: 12px;">
        <div style="flex-shrink: 0; width: 40px; height: 40px; background: #3b82f6; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px;">
          🔄
        </div>
        <div style="flex: 1;">
          <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111827;">
            Nova versão disponível
          </h3>
          <p style="margin: 4px 0 0; font-size: 14px; color: #6b7280;">
            Uma atualização está disponível. Recarregue a página para obter a versão mais recente.
          </p>
        </div>
      </div>
      <div style="display: flex; gap: 8px; justify-content: flex-end;">
        <button id="dismiss-update" style="padding: 8px 16px; background: transparent; border: 1px solid #e5e7eb; border-radius: 6px; cursor: pointer; font-size: 14px; color: #6b7280;">
          Depois
        </button>
        <button id="reload-update" style="padding: 8px 16px; background: #3b82f6; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500;">
          Recarregar agora
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(notification);

  // Add event listeners
  const reloadBtn = notification.querySelector('#reload-update');
  const dismissBtn = notification.querySelector('#dismiss-update');

  reloadBtn?.addEventListener('click', () => {
    window.location.reload();
  });

  dismissBtn?.addEventListener('click', () => {
    notification.remove();
  });

  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove();
    }
  }, 30000);
};

/**
 * Start periodic version checking
 */
export const startVersionCheck = () => {
  // Initial check after 1 minute
  setTimeout(async () => {
    const hasNewVersion = await checkForNewVersion();
    if (hasNewVersion) {
      // showUpdateNotification();
    }
  }, 60000);

  // Periodic checks
  setInterval(async () => {
    const hasNewVersion = await checkForNewVersion();
    if (hasNewVersion) {
      // showUpdateNotification();
    }
  }, VERSION_CHECK_INTERVAL);

  console.log('[VersionCheck] Version checking started');
};

/**
 * Force reload if chunk loading fails
 * This is called by the lazy loading retry mechanism
 */
export const handleChunkError = () => {
  console.warn('[VersionCheck] Chunk loading failed, clearing cache and reloading...');

  // Clear the version fingerprint to force fresh check
  localStorage.removeItem(VERSION_KEY);

  // Reload the page
  window.location.reload();
};
